import { useEffect, useMemo, useRef, useState } from 'react';
import { DeepReadonly } from 'ts-essentials';

type ItemId = string;

export interface PaginationOptions<TItem> {
  /** The direction elements will be added in, defaults to vertical. */
  elementFlow?: 'vertical' | 'horizontal';

  /** If items in multi page scenarios should directly be replaced with most current item with same id. */
  directlyUpdateItemsWithMostCurrentItems?: boolean;

  /** Sets if only the first page should be handled and following pages discarded */
  singlePage?: boolean;

  /** Function to be used to re-sort items when changing page. */
  pageChangeSortingFunction?: (
    first: DeepReadonly<TItem>,
    second: DeepReadonly<TItem>,
  ) => number;
}

const ROUNDING_ERROR_MARGIN = 0.1; // Rounding error margin due to floating point precision issues in Firefox

export interface Pagination<TItem> {
  items: DeepReadonly<TItem[]>;
  itemsForCurrentPage: Array<[DeepReadonly<TItem>, string]>;
  itemIdPages: Array<ItemId[]>;
  currentPageIndex: number;
  pages: number;
  allItemsFitAtFullSizeForCurrentPage: boolean;
  nextPage: () => void;
  pageId: string | undefined;
}

export default function usePagination<
  TItem extends object,
  TElement extends HTMLElement,
>(
  availableSize: number | undefined,
  mostCurrentItems: DeepReadonly<TItem[]>,
  itemElementsById: Map<ItemId, TElement>,
  idPropertyName: keyof DeepReadonly<TItem>,
  {
    elementFlow,
    directlyUpdateItemsWithMostCurrentItems,
    singlePage,
    pageChangeSortingFunction,
  }: PaginationOptions<TItem> = {},
): Pagination<TItem> {
  if (availableSize !== undefined && isNaN(availableSize)) {
    throw new Error(
      `Expected availableSize to be a number or undefined, but got NaN.`,
    );
  }

  const [items, setItems] = useState<DeepReadonly<TItem[]>>(mostCurrentItems);
  const itemsById = useMemo(() => {
    return new Map<ItemId, DeepReadonly<TItem>>(
      items.map((item) => [item[idPropertyName] as ItemId, item]),
    );
  }, [idPropertyName, items]);
  const [currentPageIndex, setCurrentPageIndex] = useState(0);
  const [itemIdPages, setItemIdPages] = useState<Array<ItemId[]>>([[]]);
  const [itemIdsShown, setItemIdsShown] = useState<ItemId[]>([]);
  const previousAvailableSizeRef = useRef(availableSize);
  const previousElementFlowRef = useRef(elementFlow);
  const pages = itemIdPages.length;
  const [paginationItemIdSuffix, setPaginationItemIdSuffix] = useState(0); // Pagination item id suffix is used as suffix because items might move from last page to first, which would mess up animations as the item would already be present on first page at start
  const itemsForCurrentPage = useMemo(() => {
    const idsForCurrentPage = itemIdPages[currentPageIndex];
    if (idsForCurrentPage === undefined) {
      return [];
    }
    return idsForCurrentPage.reduce((acc, itemId) => {
      const item = itemsById.get(itemId);
      if (item) {
        acc.push([item, itemId + paginationItemIdSuffix.toString()]);
      }
      return acc;
    }, new Array<[DeepReadonly<TItem>, string]>());
  }, [currentPageIndex, itemIdPages, itemsById, paginationItemIdSuffix]);
  const [allItemsFitAtFullSize, setAllItemsFitAtFullSize] = useState<boolean[]>(
    [true],
  );
  const allItemsFitAtFullSizeForCurrentPage = useMemo(
    () => allItemsFitAtFullSize[currentPageIndex] === true,
    [allItemsFitAtFullSize, currentPageIndex],
  );
  const pageId = useMemo(() => {
    if (pages <= 1) {
      return undefined;
    }
    const idsForCurrentPage = itemIdPages[currentPageIndex];
    if (idsForCurrentPage === undefined || idsForCurrentPage.length === 0) {
      return undefined;
    }

    return `${currentPageIndex}:${idsForCurrentPage.join(',')}`;
  }, [currentPageIndex, itemIdPages, pages]);

  useEffect(() => {
    if (pages <= 1) {
      setItems(mostCurrentItems);
    } else if (directlyUpdateItemsWithMostCurrentItems) {
      setItems((items) => {
        const newItems = [...items];
        newItems.forEach((item, itemIndex) => {
          const mostCurrentItem = mostCurrentItems.find(
            (mostCurrentItemCandidate) =>
              mostCurrentItemCandidate[idPropertyName] === item[idPropertyName],
          );
          if (!mostCurrentItem) {
            return;
          }

          newItems[itemIndex] = mostCurrentItem;
        });
        return newItems;
      });
    }
  }, [
    directlyUpdateItemsWithMostCurrentItems,
    idPropertyName,
    mostCurrentItems,
    pages,
  ]);

  useEffect(() => {
    const layoutConstraintChanged =
      previousAvailableSizeRef.current !== availableSize ||
      previousElementFlowRef.current !== elementFlow;
    previousAvailableSizeRef.current = availableSize;
    previousElementFlowRef.current = elementFlow;

    // Items are otherwise frozen during multi-page paging, but a change to the
    // layout requires recalculation with potentially removed items
    // so resync to the most current items to allow additions and removals.
    if (layoutConstraintChanged) {
      setItems(mostCurrentItems);
    }
  }, [availableSize, elementFlow, mostCurrentItems]);

  useEffect(() => {
    const itemElementsResizeObserver = new ResizeObserver(() => {
      recalculatePagination();
    });
    for (const itemElement of itemElementsById.values()) {
      itemElementsResizeObserver.observe(itemElement);
    }

    recalculatePagination();

    function recalculatePagination(): void {
      if (availableSize === undefined || !itemsAndElementsAreSynced()) {
        return;
      }

      const newItemIdsPages = new Array<ItemId[]>();
      const newAllItemsFitAtFullSize = new Array<boolean>();
      let itemIds = items.map((item) => getItemId(item));

      let elementGap = 0;
      const firstItemId = itemIds[0];
      if (firstItemId) {
        const firstItemElement = itemElementsById.get(firstItemId);

        if (firstItemElement?.parentElement) {
          const gapPropertyName =
            !elementFlow || elementFlow === 'vertical'
              ? 'row-gap'
              : 'column-gap';
          const elementGapValue = window
            .getComputedStyle(firstItemElement.parentElement)
            .getPropertyValue(gapPropertyName);
          elementGap =
            elementGapValue && elementGapValue !== 'normal'
              ? parseFloat(elementGapValue)
              : 0;
        }
      }

      while (itemIds.length > 0) {
        const newPage = new Array<{
          id: ItemId;
          minSize: number;
          size: number;
        }>();
        let sizeUsedForPage = 0;

        for (const itemId of itemIds) {
          const itemElement = itemElementsById.get(itemId);

          if (!itemElement) {
            throw new Error(
              `Could not find element for item with id ${itemId}, items and elements are expected to be in sync.`,
            );
          }

          const itemElementSize = itemElement.getBoundingClientRect();
          const itemElementMinHeight = getComputedStyle(itemElement).minHeight;
          const itemElementMinHeightPixels =
            itemElementMinHeight !== 'auto'
              ? parseFloat(itemElementMinHeight)
              : undefined;
          const itemElementHeight =
            itemElementMinHeightPixels && itemElementMinHeightPixels > 0
              ? itemElementMinHeightPixels
              : itemElementSize.height;
          const nextItemMinSize =
            !elementFlow || elementFlow === 'vertical'
              ? itemElementHeight
              : itemElementSize.width;
          const nextItemSize =
            !elementFlow || elementFlow === 'vertical'
              ? itemElementSize.height
              : itemElementSize.width;

          const itemSize = {
            id: itemId,
            minSize: nextItemMinSize,
            size: nextItemSize,
          };

          const totalMinSizes = newPage.reduce(
            (sum, item) => sum + item.minSize,
            0,
          );
          const largestItemSize = newPage.reduce(
            (largest, itemSize) =>
              largest === undefined || itemSize.size > largest.size
                ? itemSize
                : largest,
            newPage[0],
          );

          const totalCurrentGap = Math.max(0, newPage.length - 1) * elementGap;
          const nextItemAddedGap = newPage.length > 0 ? elementGap : 0;

          const nextItemFitWhileExpanded =
            totalMinSizes +
              totalCurrentGap +
              nextItemAddedGap +
              nextItemSize -
              ROUNDING_ERROR_MARGIN <=
            availableSize;

          const nextItemFitWhileLargestItemExpanded =
            largestItemSize !== undefined &&
            totalMinSizes +
              totalCurrentGap -
              largestItemSize.minSize +
              largestItemSize.size +
              nextItemAddedGap +
              nextItemMinSize -
              ROUNDING_ERROR_MARGIN <=
              availableSize;

          // Minimum one item per page to avoid infinite loop
          if (
            newPage.length > 0 &&
            (!nextItemFitWhileExpanded || !nextItemFitWhileLargestItemExpanded)
          ) {
            break;
          }

          sizeUsedForPage +=
            nextItemSize + (newPage.length > 0 ? elementGap : 0);
          newPage.push(itemSize);
        }

        const newPageItemIds = newPage.map((item) => item.id);
        newItemIdsPages.push(newPageItemIds);
        newAllItemsFitAtFullSize.push(sizeUsedForPage <= availableSize);

        if (singlePage) {
          break;
        }

        itemIds = itemIds.filter((itemId) => !newPageItemIds.includes(itemId));
      }

      let newPageIndex = 0;

      // Skip already shown pages
      if (newItemIdsPages.length > 1 && itemIdsShown.length > 0) {
        for (
          let pageIndex = 0;
          pageIndex < newItemIdsPages.length - 1;
          pageIndex++
        ) {
          const itemIdsForPage = newItemIdsPages[pageIndex];
          if (
            itemIdsForPage !== undefined &&
            itemIdsForPage.length > 0 &&
            itemIdsForPage.every((pageItemId) =>
              itemIdsShown.includes(pageItemId),
            )
          ) {
            newPageIndex++;
          } else {
            break;
          }
        }
      }

      setAllItemsFitAtFullSize(newAllItemsFitAtFullSize);

      setItemIdPages(newItemIdsPages);

      setCurrentPageIndex(newPageIndex);
    }

    function itemsAndElementsAreSynced(): boolean {
      if (items.length !== itemElementsById.size) {
        return false;
      }

      for (const item of items) {
        const itemId = getItemId(item);
        if (!itemElementsById.get(itemId)) {
          return false;
        }
      }

      return true;
    }

    function getItemId(item: DeepReadonly<TItem>): string {
      const itemId = item[idPropertyName];
      if (typeof itemId !== 'string') {
        throw new Error(`Item id property is expected to be of string type.`);
      }
      if (!itemId) {
        throw new Error(
          `Encountered an item without id property for ${idPropertyName.toString()}.`,
        );
      }
      return itemId;
    }

    return () => {
      itemElementsResizeObserver.disconnect();
    };
  }, [
    availableSize,
    elementFlow,
    idPropertyName,
    itemElementsById,
    itemIdsShown,
    items,
    singlePage,
  ]);

  function nextPage() {
    if (currentPageIndex + 1 >= itemIdPages.length) {
      // Only re-calculate if more than one page
      if (pages > 1) {
        returnToFirstPage();
      }
    } else {
      if (pageChangeSortingFunction) {
        setItemIdPages((itemIdPages) => {
          const newItemIdPages = structuredClone(itemIdPages);
          const itemIdsForNextPage = newItemIdPages[currentPageIndex + 1];
          if (itemIdsForNextPage) {
            itemIdsForNextPage.sort((firstId, secondId) => {
              const firstItem = itemsById.get(firstId);
              const secondItem = itemsById.get(secondId);

              if (!firstItem || !secondItem) {
                throw new Error('Expected items not found.');
              }

              return pageChangeSortingFunction(firstItem, secondItem);
            });
          }

          return newItemIdPages;
        });
        const newItemIdsOrder = new Array<ItemId>().concat(...itemIdPages);
        setItems(
          newItemIdsOrder.map((itemId) => {
            const item = itemsById.get(itemId);
            if (!item) {
              throw new Error('Expected item not found.');
            }
            return item;
          }),
        );
      }

      setItemIdsShown((itemIdsShown) => {
        const itemIdsForCurrentPage = itemIdPages[currentPageIndex] ?? [];
        return itemIdsShown.concat(itemIdsForCurrentPage);
      });
      setCurrentPageIndex(currentPageIndex + 1);
    }
  }

  function returnToFirstPage(): void {
    setCurrentPageIndex(0);

    setPaginationItemIdSuffix(
      (paginationItemIdSuffix) => (paginationItemIdSuffix + 1) % 2,
    );

    setItems(mostCurrentItems);

    setItemIdsShown([]);

    setItemIdPages([[]]);
  }

  return {
    items,
    itemsForCurrentPage,
    itemIdPages,
    currentPageIndex,
    pages,
    allItemsFitAtFullSizeForCurrentPage,
    nextPage,
    pageId,
  };
}
